import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useCallback, useEffect, useRef, useState } from 'react'
import type React from 'react'

interface TruncatedCellProps {
	children: React.ReactNode
	maxWidth?: number | string // Allow custom max width
	className?: string
	align?: 'left' | 'center' | 'right'
	showTooltip?: boolean
	tooltipSide?: 'top' | 'bottom' | 'left' | 'right'
	maxTooltipWidth?: string
	tooltipContent?: string
	/**
	 * Override the default collision boundary. Useful when the cell is inside an `overflow-hidden` container
	 * (e.g., a bottom bar or drawer) that forces the tooltip to incorrectly flip its position.
	 */
	collisionBoundary?: Element | null | undefined
}

export function TruncatedCell({
	children,
	maxWidth,
	className = '',
	align = 'left',
	showTooltip = true,
	tooltipSide = 'top',
	maxTooltipWidth = 'max-w-md',
	tooltipContent,
	collisionBoundary,
}: TruncatedCellProps) {
	const textRef = useRef<HTMLDivElement>(null)
	const [isOverflowing, setIsOverflowing] = useState(false)

	const checkOverflow = useCallback(() => {
		const element = textRef.current
		if (!element) {
			return
		}
		setIsOverflowing(element.scrollWidth > element.clientWidth)
	}, [])

	// ResizeObserver to handle all overflow checks
	useEffect(() => {
		const element = textRef.current
		if (!element) {
			return
		}

		// Initial check with small delay to ensure DOM is settled
		const initialCheck = () => checkOverflow()
		setTimeout(initialCheck, 0)

		// ResizeObserver for element size changes
		const resizeObserver = new ResizeObserver(() => {
			setTimeout(checkOverflow, 0)
		})
		resizeObserver.observe(element)

		// MutationObserver for content changes (important for dynamic columns)
		const mutationObserver = new MutationObserver(() => {
			setTimeout(checkOverflow, 0)
		})
		mutationObserver.observe(element, {
			childList: true,
			subtree: true,
			characterData: true,
		})

		return () => {
			resizeObserver.disconnect()
			mutationObserver.disconnect()
		}
	}, [checkOverflow])

	const alignmentClass = {
		left: 'text-left',
		center: 'text-center',
		right: 'text-right',
	}[align]

	// Always apply truncation styles to prevent mount jitter. The Tooltip handles showing full text.
	const truncateStyles = 'overflow-hidden text-ellipsis whitespace-nowrap'

	const content = (
		<div
			ref={textRef}
			className={`block w-full min-w-0 ${truncateStyles} ${alignmentClass} ${className}`}
			style={
				maxWidth !== undefined ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth } : undefined
			}>
			{children}
		</div>
	)

	// Only show tooltip if text is overflowing and showTooltip is true
	if (!isOverflowing || !showTooltip) {
		return content
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>{content}</TooltipTrigger>
			<TooltipContent
				side={tooltipSide}
				align='start'
				className={`${maxTooltipWidth} wrap-break-word`}
				sideOffset={8}
				avoidCollisions={true}
				// Use the custom boundary if provided, otherwise fallback to local containers or document body.
				// Explicitly checking `!== undefined` to allow passing `null` to bypass local boundaries.
				collisionBoundary={
					collisionBoundary !== undefined
						? collisionBoundary || undefined
						: textRef.current?.closest('[class*="bg-card"]') ||
							textRef.current?.closest('[class*="overflow-hidden"]') ||
							textRef.current?.closest('.CommonTable') ||
							document.body
				}
				collisionPadding={10}>
				{tooltipContent ? <p>{tooltipContent}</p> : <div>{children}</div>}
			</TooltipContent>
		</Tooltip>
	)
}
